import { AudioWaveform, Captions, LoaderCircle, Lock, Plus, Sparkle, Spline, Type, VolumeX } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent,
} from "react";

import type { EffectKind, TransitionKind } from "../../api/generated/model";
import { captionText } from "./captions/words";
import { EFFECT_DRAG_TYPE, EFFECTS, effectName } from "./effect-catalog";
import { VideoFrame } from "../../ui/VideoFrame";
import type { SourceMedia } from "./media";
import { sourceKey } from "./media";
import { useStripImage } from "./clip-strip";
import { clipsChosen, isChosen, only, toggled, type Selection } from "./selection";
import type { MenuPlace } from "./TimelineMenu";
import {
  beatAt,
  beatTimes,
  clipMs,
  clipsOn,
  junctionsOf,
  newId,
  overlaps,
  showsAt,
  totalMs,
  trackName,
  TRACK_OF,
  type AudioClip,
  type Clip,
  type Junction,
  type Timeline,
  type TimelineEdit,
  type Track,
} from "./timeline";
import { MagnetMark } from "./MagnetMark";
import { MarkerFlags } from "./MarkerFlags";
import { curveLabel } from "./CurveTiles";
import { speedText } from "./SpeedParams";
import { VolumeLine } from "./VolumeLine";
import { SNAP_PX, snapEdge, snapMove, snapStops, type Snapped } from "./snap";
import { groupMove, shiftText, type GroupMove } from "./group-move";
import { formatClock, formatTimecode } from "./time";
import { TEXT_DRAG_TYPE } from "./text-presets";
import { TRANSITION_DRAG_TYPE, drawnKind, transitionName } from "./transition-catalog";
import { takesTransition } from "./transition-drop";
import { LONG_PRESS_SLOP_PX, zoomLabel } from "./touch-timeline";
import { useTouchTimeline, type TouchDrag } from "./use-touch-timeline";
import classes from "./editor.module.css";
import cutClasses from "./transitions.module.css";

/** The timeline's scale at 100% zoom: 100 px a second (live opens at 115%, 117 px). */
export const PX_PER_SECOND = 100;
const MIN_LANE_MS = 10_000;
/** Ruler label spacings to choose from, in seconds: the first that leaves 64px between labels. */
const LABEL_STEPS = [1, 2, 5, 10, 15, 30, 60] as const;
/** What Alt with an arrow moves a block by; with Shift too, a second. */
const NUDGE_MS = 100;
/** How far a press on a band's empty space travels before it draws a box (the canvas's 3 px). */
const BOX_FROM_PX = 3;

/** Whether a clip shows frames of its source: a poster, or a URL to take one from. */
function hasFrames(clip: Clip, source: SourceMedia | undefined): boolean {
  if (clip.kind === "audio" || clip.kind === "text") return false;
  return source !== undefined && (source.poster !== null || source.url !== null);
}

/**
 * The frames behind a clip's name, as live's filmstrip: one small frame repeated along the clip,
 * the source's poster (a still's picture) or else the video's first frame once drawn
 * (`clip-strip.ts`, issue 349); until then one frame of the video itself (VideoFrame, still).
 * Decoration only.
 */
function ClipFrames({ source }: { source: SourceMedia | undefined }) {
  const strip = useStripImage(source);
  if (source === undefined) return null;
  if (strip !== null) {
    return (
      <span
        className={classes.clipFrames}
        style={{ backgroundImage: `url("${strip}")` }}
        data-strip
        aria-hidden
      />
    );
  }
  if (source.url === null) return null;
  return (
    <span className={classes.clipFrames} aria-hidden>
      <VideoFrame src={source.url} still fallback={null} />
    </span>
  );
}

/**
 * A sound's beats that show (spec beats.md): dots along its block's lower edge, every fourth of
 * its list larger, `pxPerMs` apart a millisecond. Two paths, whatever their number. Decoration:
 * the block's label counts them.
 */
function BeatDots({ clip, pxPerMs }: { clip: AudioClip; pxPerMs: number }) {
  let small = "";
  let large = "";
  clip.beatsMs.forEach((beat, index) => {
    const atMs = beatAt(clip, beat);
    if (!showsAt(clip, atMs)) return;
    const dot = `M${((atMs - clip.startMs) * pxPerMs).toFixed(1)} 4h0`;
    if (index % 4 === 0) large += dot;
    else small += dot;
  });
  if (small === "" && large === "") return null;
  return (
    <svg className={classes.beats} aria-hidden data-beats>
      <path className={classes.beat} d={small} />
      <path className={classes.beatLarge} d={large} />
    </svg>
  );
}

/** How many of a sound's beats show, for its block's label: ", 60 beats", or nothing. */
function beatsSaid(clip: AudioClip): string {
  const count = beatTimes(clip).length;
  if (count === 0) return "";
  return count === 1 ? ", 1 beat" : `, ${count} beats`;
}

/**
 * Space on a block plays or pauses (the editor's key handler), so its key-up must not also press
 * the block, which a button does on Space.
 */
function noSpaceClick(event: KeyboardEvent<HTMLButtonElement>): void {
  if (event.key === " ") event.preventDefault();
}

/** A block's word for a screen reader: live's clips, sounds, titles and effects. */
const NOUN: Record<Clip["kind"], string> = {
  video: "Clip",
  image: "Clip",
  audio: "Sound",
  text: "Title",
  effect: "Effect",
  caption: "Caption",
};

/** A clip's file, for a clip that has one. */
function sourceOf(clip: Clip, media: ReadonlyMap<string, SourceMedia>): SourceMedia | undefined {
  return clip.kind === "video" || clip.kind === "image" || clip.kind === "audio"
    ? media.get(sourceKey(clip.source))
    : undefined;
}

/** A clip's name and what it is, for its button; `removing` while its background is being removed. */
function label(clip: Clip, number: number, media: ReadonlyMap<string, SourceMedia>, removing = false): string {
  const when = `${formatClock(clip.startMs)} to ${formatClock(clip.endMs)}`;
  const locked = clip.locked ? ", locked" : "";
  const hidden = clip.hidden ? ", hidden" : "";
  if (clip.kind === "text") {
    return `Title: ${clip.text === "" ? "empty" : clip.text}, ${when}${hidden}${locked}`;
  }
  if (clip.kind === "effect") return `Effect: ${effectName(clip.effect)}, ${when}${hidden}${locked}`;
  if (clip.kind === "caption") {
    const said = captionText(clip.words).replace(/\s+/g, " ");
    return `Caption: ${said === "" ? "empty" : said}, ${when}${hidden}${locked}`;
  }
  const name = sourceOf(clip, media)?.name ?? `${NOUN[clip.kind]} ${number}`;
  const muted = (clip.kind === "video" || clip.kind === "audio") && clip.muted ? ", muted" : "";
  const beats = clip.kind === "audio" ? beatsSaid(clip) : "";
  const busy = removing ? ", removing background" : "";
  return `${NOUN[clip.kind]} ${number}: ${name}, ${formatClock(clipMs(clip))}${beats}${muted}${hidden}${locked}${busy}`;
}

export interface TracksProps {
  timeline: Timeline;
  media: ReadonlyMap<string, SourceMedia>;
  selection: Selection | null;
  playheadMs: number;
  onSelect: (selection: Selection | null) => void;
  onEdit: (edit: TimelineEdit) => void;
  onSeek: (ms: number) => void;
  /** Opens live's timeline menu at a place, on a clip or (null) on the empty timeline. */
  onMenu: ((place: MenuPlace) => void) | undefined;
  /** The member may not change this project: no drag, no handles, no editing keys. */
  readOnly: boolean;
  /** Pixels a second: the zoom (`PX_PER_SECOND` at 100%). */
  pxPerSecond: number;
  /** Live's "+" on a cut without a transition: a 2 s Fade there (absent when read-only). */
  onAddTransition: ((junction: Junction) => void) | undefined;
  /** A Transitions tab tile being dragged: every cut shows its mark, dashed, to drop it on. */
  droppingTransition: boolean;
  /** A tile let go on a track: its kind on the track's cut nearest the pointer. */
  onDropTransition: (junction: Junction, kind: TransitionKind) => void;
  /** An effect tile let go anywhere on the timeline (#265): live's, the same as its click. */
  onDropEffect: (kind: EffectKind) => void;
  /** A text tile or "Add Text" let go on the timeline (issue 241): its drag's data, at the pointer's time. */
  onDropText: (data: string, atMs: number) => void;
  /** Live's "Attach" (N): off, a dragged or trimmed edge snaps to nothing (spec capcut-shortcuts.md). */
  snapping: boolean;
  /** The preview axis (S): a line, and its time, follows the pointer over the tracks. */
  previewAxis: boolean;
  /** The preview axis's time as the pointer moves, or null off the tracks or with the axis off. */
  onHover: (ms: number | null) => void;
  /**
   * On a phone (spec phone-timeline.md): the zoom two fingers set. Absent, the desktop's
   * timeline, which nothing here changes.
   */
  touch?: { zoom: number; onZoom: (zoom: number) => void } | undefined;
  /** The clips whose background is being removed (spec remove-background.md): a spinning mark each. */
  removing?: ReadonlySet<string>;
}

/** Live's mark for a cut with a transition: two white triangles pointing at the cut. */
function CutIcon() {
  return (
    <svg width={12} height={10} viewBox="-6 -5 12 10" fill="currentColor" aria-hidden>
      <path d="M-6 -5 L-1 0 L-6 5 Z M6 -5 L1 0 L6 5 Z" />
    </svg>
  );
}

/** A clip being dragged, and where on the clip the pointer took it. */
interface Drag {
  id: string;
  grabMs: number;
}

/**
 * The timeline (spec E2's tracks): a ruler that seeks, then each track, top to bottom, as a band
 * with its clips placed at their times. Every block is a button: focus or a click selects it,
 * Delete removes it, Alt with an arrow nudges it. A block drags to another time, or to another
 * track of its kind, through `moveClip`, or onto live's guide above, between or below the tracks
 * for a new track there, through `moveToNewTrack`; the selected block's edges drag to trim it, through
 * `trimStart` and `trimEnd` (the inspector's fields are the same edits without a pointer).
 */
export function Tracks(props: TracksProps) {
  const { timeline, media, selection, playheadMs, onSelect, onEdit, onSeek, onMenu, pxPerSecond } =
    props;
  const px = (ms: number) => (ms / 1000) * pxPerSecond;
  const msAt = (pixels: number) => (pixels / pxPerSecond) * 1000;
  // The lanes' left edge is the timeline's 0, wherever it has scrolled to.
  const lanes = useRef<HTMLDivElement>(null);
  /**
   * The one way from a pointer's place to the timeline's time (issue 828 bug 32): its distance from
   * the lanes' left edge, at the zoom. The ruler, the bands, the guides and every drop take it.
   */
  const timeAt = (clientX: number) =>
    Math.max(0, msAt(clientX - (lanes.current?.getBoundingClientRect().left ?? 0)));
  const total = totalMs(timeline);
  // The lanes reach the last marker too (spec markers), so none lies out of reach past the clips.
  const laneMs = Math.max(MIN_LANE_MS, total, ...timeline.markers.map((marker) => marker.atMs)) + 2000;
  const labelStep = LABEL_STEPS.find((step) => step * pxPerSecond >= 64) ?? 60;
  // Small ticks four to a label at most, none closer than 12px.
  const tickMs = Math.max((labelStep * 1000) / 4, Math.ceil(12_000 / pxPerSecond / 250) * 250);
  const [dragging, setDragging] = useState<Drag | null>(null);
  const [landing, setLanding] = useState<{ trackId: string; startMs: number } | null>(null);
  // The guide between tracks under the dragged clip: where in the stack a new track would go.
  const [newTrackAt, setNewTrackAt] = useState<number | null>(null);
  // Live's guide line, where a dragged or trimmed edge has snapped.
  const [guideMs, setGuideMs] = useState<number | null>(null);
  // The new-track zones open just after a drag starts: a layout change inside `dragstart` itself
  // moves the block under the pointer, which can end the browser's drag at once.
  const [zonesShown, setZonesShown] = useState(false);
  const zonesTimer = useRef<number | undefined>(undefined);
  const draggedClip =
    dragging === null ? undefined : timeline.clips.find((clip) => clip.id === dragging.id);
  // Live's snapping distance, or none with Snapping off: no stop is ever that near.
  const snapMs = props.snapping ? msAt(SNAP_PX) : 0;
  // One of several chosen clips dragged: they all move by its travel, each on its own track (spec
  // group-moves.md); where they would land, and whether the reducer would make it.
  const chosenIds = clipsChosen(selection, timeline)?.ids ?? [];
  const group = dragging !== null && chosenIds.length > 1 && chosenIds.includes(dragging.id) ? chosenIds : null;
  const [groupLanding, setGroupLanding] = useState<GroupMove | null>(null);
  // The box a press on a band's empty space draws, in the lanes' pixels.
  const [box, setBox] = useState<{ left: number; top: number; width: number; height: number } | null>(null);

  // The preview axis: the pointer's time over the tracks, told at most once a frame; none while a
  // clip is dragged (the browser's drag sends no pointer moves) or with the axis off.
  const [axisMs, setAxisMs] = useState<number | null>(null);
  const axisAsk = useRef<{ frame: number | null; ms: number | null }>({ frame: null, ms: null });
  const { onHover, previewAxis } = props;
  const showAxis = useCallback(
    (ms: number | null) => {
      const ask = axisAsk.current;
      ask.ms = ms;
      if (ask.frame !== null) return;
      ask.frame = requestAnimationFrame(() => {
        ask.frame = null;
        setAxisMs(ask.ms);
        onHover(ask.ms);
      });
    },
    [onHover],
  );
  useEffect(() => {
    if (!previewAxis || dragging !== null) showAxis(null);
  }, [previewAxis, dragging, showAxis]);
  useEffect(() => {
    const ask = axisAsk.current;
    return () => {
      // Cleared as well as cancelled: React may mount the timeline again (StrictMode does).
      if (ask.frame !== null) cancelAnimationFrame(ask.frame);
      ask.frame = null;
    };
  }, []);

  /**
   * A drag's stops, made once a drag: the timeline holds still while a clip is dragged, and a
   * sound's beats make thousands (spec beats.md), so each pointer move only scans them.
   */
  const dragStops = useRef<{ except: string; timeline: Timeline; playheadMs: number; stops: number[] } | null>(null);
  const stopsOf = (except: string | readonly string[]): number[] => {
    const key = typeof except === "string" ? except : except.join(" ");
    const held = dragStops.current;
    if (held?.except === key && held.timeline === timeline && held.playheadMs === playheadMs) return held.stops;
    const stops = snapStops(timeline, except, playheadMs);
    dragStops.current = { except: key, timeline, playheadMs, stops };
    return stops;
  };
  /**
   * Where the dragged clip would start, from the pointer's place on a band or a guide, glued to
   * live's stops: on `trackId` only where it is free of the track's other clips; on a new track
   * (null) anywhere.
   */
  const landAt = (clientX: number, drag: Drag, trackId: string | null): Snapped => {
    const at = Math.max(0, Math.round(timeAt(clientX) - drag.grabMs));
    const clip = timeline.clips.find((each) => each.id === drag.id);
    if (clip === undefined) return { ms: at, guideMs: null };
    const free = (startMs: number) =>
      trackId === null || !overlaps(timeline, trackId, clip.id, startMs, startMs + clipMs(clip));
    return snapMove(stopsOf(clip.id), clip, at, snapMs, free);
  };
  /**
   * What a clip let go on a band does: a move to its landing, glued to live's stops; where that
   * would lie over another clip of the band and the pointer is on one, Daniil's reorder (issue 828
   * bug 28 item 3): it takes that clip's place, and the clips between make room.
   */
  const dropOn = (clientX: number, drag: Drag, track: Track): Snapped & { edit: TimelineEdit } => {
    const at = landAt(clientX, drag, track.id);
    const move = { ...at, edit: { type: "moveClip", id: drag.id, startMs: at.ms, trackId: track.id } } as const;
    const clip = timeline.clips.find((each) => each.id === drag.id);
    if (clip === undefined || !overlaps(timeline, track.id, clip.id, at.ms, at.ms + clipMs(clip))) return move;
    const pointerMs = timeAt(clientX);
    const under = clipsOn(timeline, track.id, clip.id).find((each) => showsAt(each, pointerMs));
    if (under === undefined) return move;
    const ms = clip.trackId === track.id && under.startMs > clip.startMs ? under.endMs - clipMs(clip) : under.startMs;
    return { ms, guideMs: null, edit: { type: "reorderClip", id: clip.id, ontoId: under.id } };
  };
  /**
   * A drag of one of several chosen clips: their move by its travel, its landing glued to live's
   * stops as a single clip's is, but never to the group's own edges, which move with it.
   */
  const groupDrop = (clientX: number, drag: Drag, ids: readonly string[]) => {
    const clip = timeline.clips.find((each) => each.id === drag.id);
    if (clip === undefined) return { byMs: 0, edit: null, guideMs: null };
    const at = Math.max(0, Math.round(timeAt(clientX) - drag.grabMs));
    const snapped = snapMove(stopsOf(ids), clip, at, snapMs, () => true);
    return { ...groupMove(timeline, ids, snapped.ms - clip.startMs), guideMs: snapped.guideMs };
  };
  const endDrag = () => {
    setDragging(null);
    setLanding(null);
    setGroupLanding(null);
    setNewTrackAt(null);
    setGuideMs(null);
    window.clearTimeout(zonesTimer.current);
    setZonesShown(false);
  };

  // A drag over a band or a guide, and its drop there, at a pointer's x: the browser's drag and a
  // finger's (spec phone-timeline.md) both come here, so they land and edit alike.
  const overBand = (track: Track, clientX: number) => {
    if (dragging === null) return;
    setNewTrackAt(null);
    if (group !== null) {
      const moved = groupDrop(clientX, dragging, group);
      setGuideMs(moved.guideMs);
      setGroupLanding(moved);
      return;
    }
    const at = dropOn(clientX, dragging, track);
    setGuideMs(at.guideMs);
    setLanding({ trackId: track.id, startMs: at.ms });
  };
  const dropOnBand = (track: Track, clientX: number) => {
    if (dragging === null) return;
    if (group === null) onEdit(dropOn(clientX, dragging, track).edit);
    else {
      // Made whole, or not at all.
      const { edit } = groupDrop(clientX, dragging, group);
      if (edit !== null) onEdit(edit);
    }
    endDrag();
  };
  const overGuide = (index: number, clientX: number) => {
    if (dragging === null) return;
    setLanding(null);
    setNewTrackAt(index);
    setGuideMs(landAt(clientX, dragging, null).guideMs);
  };
  const dropOnGuide = (index: number, clientX: number) => {
    if (dragging === null) return;
    onEdit({ type: "moveToNewTrack", id: dragging.id, startMs: landAt(clientX, dragging, null).ms, index, newTrackId: newId() });
    endDrag();
  };

  /**
   * A trim handle's drag: the pointer's travel moves the clip's start or end by as much time,
   * glued to live's stops, the reducer keeping it inside its source and off its neighbours.
   */
  const trimFrom = (event: PointerEvent<HTMLSpanElement>, clip: Clip, edge: "in" | "out") => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const sourceMs = sourceOf(clip, media)?.durationMs ?? null;
    // A trimmed sound's own beats stay on its music: its edge glues to them (spec beats.md).
    const stops = snapStops(timeline, clip.id, playheadMs, true);
    const move = (moved: MouseEvent) => {
      const delta = msAt(moved.clientX - startX);
      const at = snapEdge(stops, (edge === "in" ? clip.startMs : clip.endMs) + delta, snapMs);
      setGuideMs(at.guideMs);
      onEdit(
        edge === "in"
          ? { type: "trimStart", id: clip.id, startMs: at.ms, origin: timeline }
          : { type: "trimEnd", id: clip.id, endMs: at.ms, sourceMs, origin: timeline },
      );
    };
    const stop = () => {
      setGuideMs(null);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
  };

  /**
   * A press on the ruler, and its drag: the playhead at the pointer's time (`timeAt`). The range
   * under it only takes the keyboard: its own mapping spans its width, which the lanes stretch
   * past the time they show, so a click there landed early (issue 828 bug 32).
   */
  const seekFrom = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.querySelector("input")?.focus();
    const seekTo = (clientX: number) => onSeek(Math.min(Math.round(timeAt(clientX)), total));
    seekTo(event.clientX);
    const move = (moved: MouseEvent) => seekTo(moved.clientX);
    const stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
  };

  const select = (clip: Clip) => onSelect(only(clip.id));
  const junctions = junctionsOf(timeline);
  const numberOf = (id: string) => {
    const clip = timeline.clips.find((each) => each.id === id);
    return clip === undefined ? 0 : clipsOn(timeline, clip.trackId).indexOf(clip) + 1;
  };

  /** Live's mark on a cut (#240): "+" adds a Fade, a set one is chosen (Delete removes it). */
  const cutMark = (cut: Junction) => {
    const { transition } = cut;
    if (transition === null && props.onAddTransition === undefined) return null;
    const between = `between Clip ${numberOf(cut.fromId)} and Clip ${numberOf(cut.toId)}`;
    const chosen = transition !== null && isChosen(selection, transition.id);
    return (
      <span
        key={`cut:${cut.fromId}:${cut.toId}`}
        className={cutClasses.cut}
        style={{ left: px(cut.atMs) }}
      >
        <button
          type="button"
          className={cutClasses.mark}
          data-set={transition !== null}
          data-dropping={props.droppingTransition || undefined}
          // Live's red mark: a cut a dragged tile may not land on (`transition-drop.ts`).
          data-refused={(props.droppingTransition && !takesTransition(timeline, cut)) || undefined}
          aria-pressed={transition === null ? undefined : chosen}
          aria-label={
            transition === null
              ? `Add a transition ${between}`
              : `Transition: ${transitionName(transition.kind)}, ${(transition.durationMs / 1000).toFixed(1)} s, ${between}`
          }
          onClick={() => {
            if (transition === null) props.onAddTransition?.(cut);
            else onSelect(only(transition.id));
          }}
          onKeyDown={(event) => {
            if (transition === null || props.readOnly) return;
            if (event.key === "Delete" || event.key === "Backspace") {
              event.preventDefault();
              onEdit({ type: "removeTransition", id: transition.id });
            }
          }}
          onKeyUp={noSpaceClick}
        >
          {transition === null ? <Plus size={12} strokeWidth={2.5} aria-hidden /> : <CutIcon />}
        </button>
      </span>
    );
  };

  /**
   * A tile let go on a video track: the track's cut nearest the pointer that may take it (live's
   * red marks are passed over), if it has one.
   */
  const dropTransition = (event: DragEvent<HTMLElement>, track: Track) => {
    const kind = drawnKind(event.dataTransfer.getData(TRANSITION_DRAG_TYPE));
    const atMs = timeAt(event.clientX);
    const nearest = junctions
      .filter((cut) => cut.trackId === track.id && takesTransition(timeline, cut))
      .sort((one, other) => Math.abs(one.atMs - atMs) - Math.abs(other.atMs - atMs))[0];
    if (kind !== null && nearest !== undefined) props.onDropTransition(nearest, kind);
  };
  const carriesEffect = (event: DragEvent<HTMLElement>) =>
    Array.from(event.dataTransfer?.types ?? []).includes(EFFECT_DRAG_TYPE);
  const carriesTransition = (event: DragEvent<HTMLElement>) =>
    Array.from(event.dataTransfer?.types ?? []).includes(TRANSITION_DRAG_TYPE);
  const carriesText = (event: DragEvent<HTMLElement>) =>
    Array.from(event.dataTransfer?.types ?? []).includes(TEXT_DRAG_TYPE);
  // A press with Shift (live's ⇧-click) adds the block to the chosen or takes it out; its focus,
  // which comes first, leaves the chosen as they are.
  const shiftPress = useRef(false);
  const focusBlock = (clip: Clip) => {
    if (shiftPress.current || isChosen(selection, clip.id)) return;
    select(clip);
  };
  const clickBlock = (event: ReactMouseEvent<HTMLButtonElement>, clip: Clip) => {
    shiftPress.current = false;
    if (event.shiftKey) onSelect(toggled(clipsChosen(selection, timeline), clip.id));
    else select(clip);
  };

  const blockKeys = (event: KeyboardEvent<HTMLButtonElement>, clip: Clip) => {
    if (props.readOnly) {
      if (event.key === "Enter") select(clip);
      return;
    }
    // The menu key, or Shift+F10: live's menu for the block, at its foot.
    if (event.key === "ContextMenu" || (event.shiftKey && event.key === "F10")) {
      event.preventDefault();
      select(clip);
      const block = event.currentTarget.getBoundingClientRect();
      onMenu?.({ x: block.left, y: block.bottom, clipId: clip.id });
      return;
    }
    // A locked clip moves and goes nowhere (the reducer refuses it too).
    if (clip.locked) {
      if (event.key === "Enter") select(clip);
      return;
    }
    if (event.altKey && (event.key === "ArrowLeft" || event.key === "ArrowRight")) {
      event.preventDefault();
      const step = (event.shiftKey ? 1000 : NUDGE_MS) * (event.key === "ArrowLeft" ? -1 : 1);
      // One of several chosen: the nudge moves them all, or none (spec group-moves.md).
      if (chosenIds.length > 1 && chosenIds.includes(clip.id)) {
        const { edit } = groupMove(timeline, chosenIds, step);
        if (edit !== null) onEdit(edit);
        return;
      }
      onEdit({ type: "moveClip", id: clip.id, startMs: clip.startMs + step });
    } else if (event.key === "Delete" || event.key === "Backspace") {
      // One of several chosen: the editor's Delete takes them all.
      if (isChosen(selection, clip.id) && (selection?.ids.length ?? 0) > 1) return;
      event.preventDefault();
      onEdit({ type: "removeClip", id: clip.id, ripple: event.shiftKey });
    } else if (event.key === "Enter") {
      select(clip);
    }
  };

  /**
   * A press on a band's empty space and its drag (spec group-moves.md): a box, and every clip of a
   * track it crosses, at the times it crosses, chosen; with ⇧ they join the chosen. A press with no
   * drag chooses nothing.
   */
  const boxFrom = (event: PointerEvent<HTMLUListElement>) => {
    const area = lanes.current;
    if (event.button !== 0 || event.target !== event.currentTarget || area === null) return;
    if (props.touch !== undefined && event.pointerType === "touch") {
      // A finger on a phone scrolls the timeline (spec phone-timeline.md): it draws no box, and only
      // its tap, still, clears the choice.
      const start = { x: event.clientX, y: event.clientY };
      const off = () => {
        window.removeEventListener("pointerup", tapped);
        window.removeEventListener("pointercancel", off);
      };
      const tapped = (up: globalThis.PointerEvent) => {
        off();
        if (Math.hypot(up.clientX - start.x, up.clientY - start.y) < LONG_PRESS_SLOP_PX) onSelect(null);
      };
      window.addEventListener("pointerup", tapped);
      window.addEventListener("pointercancel", off);
      return;
    }
    event.preventDefault();
    const from = { x: event.clientX, y: event.clientY };
    const adding = event.shiftKey;
    const before = adding ? chosenIds : [];
    let drawn = false;
    const follow = (moved: globalThis.PointerEvent) => {
      if (!drawn && Math.hypot(moved.clientX - from.x, moved.clientY - from.y) < BOX_FROM_PX) return;
      drawn = true;
      const rect = area.getBoundingClientRect();
      const [x0, x1] = [Math.min(from.x, moved.clientX), Math.max(from.x, moved.clientX)];
      const [y0, y1] = [Math.min(from.y, moved.clientY), Math.max(from.y, moved.clientY)];
      setBox({ left: x0 - rect.left, top: y0 - rect.top, width: x1 - x0, height: y1 - y0 });
      const crossed = new Set(
        [...area.querySelectorAll<HTMLElement>("ul[data-track]")]
          .filter((lane) => {
            const place = lane.getBoundingClientRect();
            return place.bottom >= y0 && place.top <= y1;
          })
          .map((lane) => lane.dataset["track"]),
      );
      const [fromMs, toMs] = [timeAt(x0), timeAt(x1)];
      const boxed = timeline.clips
        .filter((clip) => crossed.has(clip.trackId) && clip.endMs > fromMs && clip.startMs < toMs)
        .map((clip) => clip.id);
      const ids = [...new Set([...before, ...boxed])];
      const [first] = ids;
      onSelect(first === undefined ? null : { id: first, ids });
    };
    const stop = () => {
      window.removeEventListener("pointermove", follow);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
      setBox(null);
      if (!drawn && !adding) onSelect(null);
    };
    window.addEventListener("pointermove", follow);
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
  };

  const band = (track: Track) => {
    const clips = clipsOn(timeline, track.id);
    const marks = junctions.filter((cut) => cut.trackId === track.id).flatMap((cut) => cutMark(cut) ?? []);
    // A group moves in time on its own tracks, over any band.
    const accepts = draggedClip !== undefined && (group !== null || TRACK_OF[draggedClip.kind] === track.kind);
    return (
      // The band's drop zone: a dragged clip lands by the pointer's time.
      <div
        key={track.id}
        className={classes.dropZone}
        onContextMenu={(event) => {
          // A block's own menu has taken it; elsewhere on the band, live's empty-timeline menu.
          if (event.defaultPrevented || onMenu === undefined) return;
          event.preventDefault();
          onMenu({ x: event.clientX, y: event.clientY, clipId: null });
        }}
        onDragOver={(event) => {
          if (carriesTransition(event)) {
            // A tile lands on a video track's cut.
            if (track.kind !== "video" || props.readOnly) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = "copy";
            return;
          }
          if (dragging === null || !accepts) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = "move";
          overBand(track, event.clientX);
        }}
        onDrop={(event) => {
          if (carriesTransition(event)) {
            if (track.kind !== "video" || props.readOnly) return;
            event.preventDefault();
            dropTransition(event, track);
            return;
          }
          if (dragging === null || !accepts) return;
          event.preventDefault();
          dropOnBand(track, event.clientX);
        }}
      >
      {track.magnet ? <MagnetMark /> : null}
      <ul
        className={classes.lane}
        data-kind={track.kind}
        data-track={track.id}
        onPointerDown={boxFrom}
        aria-label={trackName(timeline, track)}
        data-empty={clips.length === 0}
      >
        {clips.map((clip, at) => {
          const selected = isChosen(selection, clip.id);
          const source = sourceOf(clip, media);
          const frames = hasFrames(clip, source);
          return (
            <li
              key={clip.id}
              className={classes.slot}
              style={{ left: px(clip.startMs), width: px(clipMs(clip)) }}
            >
              <button
                type="button"
                data-block
                data-clip={clip.id}
                className={
                  clip.kind === "text"
                    ? classes.title
                    : clip.kind === "effect"
                      ? classes.effect
                      : clip.kind === "caption"
                        ? classes.caption
                        : classes.clip
                }
                data-kind={clip.kind}
                data-frames={frames}
                data-selected={selected}
                aria-pressed={selected}
                aria-label={label(clip, at + 1, media, props.removing?.has(clip.id) === true)}
                onPointerDown={(event) => {
                  shiftPress.current = event.shiftKey;
                }}
                onFocus={() => focusBlock(clip)}
                onClick={(event) => clickBlock(event, clip)}
                onKeyDown={(event) => blockKeys(event, clip)}
                onKeyUp={noSpaceClick}
                onContextMenu={(event) => {
                  if (onMenu === undefined) return;
                  event.preventDefault();
                  // On a chosen block the chosen stay; on another, it is chosen alone.
                  if (!isChosen(selection, clip.id)) select(clip);
                  onMenu({ x: event.clientX, y: event.clientY, clipId: clip.id });
                }}
                draggable={!props.readOnly && !clip.locked}
                data-locked={clip.locked}
                data-hidden={clip.hidden}
                data-dragging={dragging?.id === clip.id || (group?.includes(clip.id) ?? false)}
                onDragStart={(event) => {
                  const block = event.currentTarget.getBoundingClientRect();
                  event.dataTransfer.effectAllowed = "move";
                  event.dataTransfer.setData("text/plain", clip.id);
                  // One of several chosen keeps them chosen: they move together.
                  if (!isChosen(selection, clip.id)) select(clip);
                  // Where on the block the pointer took it, so it lands where it is let go.
                  const grab = Number.isFinite(event.clientX) ? event.clientX - block.left : 0;
                  setDragging({ id: clip.id, grabMs: Math.max(0, msAt(grab)) });
                  zonesTimer.current = window.setTimeout(() => setZonesShown(true), 0);
                }}
                onDragEnd={endDrag}
              >
                {clip.kind === "text" ? (
                  <>
                    <Type size={12} className={classes.blockIcon} aria-hidden />
                    <span className={classes.blockName}>{clip.text}</span>
                    {clip.locked ? <Lock size={12} className={classes.blockIcon} aria-hidden /> : null}
                  </>
                ) : clip.kind === "effect" ? (
                  <>
                    <Sparkle size={14} className={classes.blockIcon} aria-hidden />
                    <span className={classes.blockName}>{effectName(clip.effect)}</span>
                    {clip.locked ? <Lock size={12} className={classes.blockIcon} aria-hidden /> : null}
                  </>
                ) : clip.kind === "caption" ? (
                  // Live's caption bar (#239): a CC glyph and the caption's text.
                  <>
                    <Captions size={13} className={classes.captionIcon} aria-hidden />
                    <span className={classes.blockName}>{captionText(clip.words)}</span>
                    {clip.locked ? <Lock size={12} className={classes.blockIcon} aria-hidden /> : null}
                  </>
                ) : (
                  <>
                    {frames ? <ClipFrames source={source} /> : null}
                    {(clip.kind === "video" || clip.kind === "audio") && clip.volumePoints.length > 0 ? <VolumeLine clip={clip} /> : null}
                    <span className={classes.clipLabel}>
                      {clip.kind === "audio" ? (
                        <AudioWaveform size={12} className={classes.blockIcon} aria-hidden />
                      ) : null}
                      {/* A curve's mark and its name (spec clip-speed-curves-tab), else one speed not at 1×. */}
                      {(clip.kind === "video" || clip.kind === "audio") && clip.speedCurve !== null && clip.speedPoints.length >= 2 ? (
                        <span className={classes.blockMeta}>
                          <Spline size={10} className={classes.blockIcon} aria-hidden />
                          {curveLabel(clip.speedCurve)}
                        </span>
                      ) : "speed" in clip && clip.speed !== 1 ? (
                        <span className={classes.blockMeta}>{speedText(clip.speed)}</span>
                      ) : null}
                      <span className={classes.blockName}>
                        {source?.name ?? `${NOUN[clip.kind]} ${at + 1}`}
                      </span>
                      <span className={classes.blockMeta}>
                        {props.removing?.has(clip.id) === true ? (
                          <LoaderCircle size={12} className={classes.spinning} aria-hidden />
                        ) : null}
                        {clip.locked ? <Lock size={12} aria-hidden /> : null}
                        {(clip.kind === "video" || clip.kind === "audio") && clip.muted ? (
                          <VolumeX size={12} aria-hidden />
                        ) : null}
                        {formatClock(clipMs(clip))}
                      </span>
                    </span>
                    {clip.kind === "audio" ? <BeatDots clip={clip} pxPerMs={pxPerSecond / 1000} /> : null}
                  </>
                )}
              </button>
              {selected && !props.readOnly && !clip.locked ? (
                // Pointer handles for the start and the end; the inspector's fields are the same
                // edits for a keyboard and a screen reader.
                <>
                  <span
                    className={classes.handle}
                    data-edge="in"
                    aria-hidden
                    onPointerDown={(event) => trimFrom(event, clip, "in")}
                  />
                  <span
                    className={classes.handle}
                    data-edge="out"
                    aria-hidden
                    onPointerDown={(event) => trimFrom(event, clip, "out")}
                  />
                </>
              ) : null}
            </li>
          );
        })}
        {landing?.trackId === track.id && draggedClip !== undefined ? (
          <li
            className={classes.landing}
            style={{ left: px(landing.startMs), width: px(clipMs(draggedClip)) }}
            aria-hidden
          />
        ) : null}
        {group === null || groupLanding === null
          ? null
          : clips
              .filter((each) => group.includes(each.id))
              .map((each) => (
                <li
                  key={`landing:${each.id}`}
                  className={classes.landing}
                  data-group-landing
                  // Where it cannot go: its group stays.
                  data-refused={(groupLanding.byMs !== 0 && groupLanding.edit === null) || undefined}
                  style={{ left: px(each.startMs + groupLanding.byMs), width: px(clipMs(each)) }}
                  aria-hidden
                >
                  {each.id === dragging?.id ? (
                    <span className={classes.landingTag}>
                      {group.length} clips · {shiftText(groupLanding.byMs)}
                    </span>
                  ) : null}
                </li>
              ))}
      </ul>
      {marks.length > 0 ? (
        // The cuts' marks over the band, outside its list of clips; each mark names its cut.
        <div className={cutClasses.cuts}>{marks}</div>
      ) : null}
      </div>
    );
  };

  /**
   * Live's guide above the tracks, between two of them or below them (its `Helper`s, issue 828
   * bug 28): a dragged clip let go on one goes on a new track of its kind at that place in the
   * stack, from the pointer's time. The upper of two video tracks draws in front.
   */
  const newTrackGuide = (index: number) => {
    // A group never changes track: its zones stay shut.
    const open = draggedClip !== undefined && draggedClip.kind !== "caption" && !props.readOnly && group === null;
    return (
      <div
        key={`new-track:${index}`}
        className={classes.newTrack}
        data-new-track={index}
        data-edge={index === 0 ? "top" : index === timeline.tracks.length ? "bottom" : undefined}
        data-open={open || undefined}
        data-shown={(open && zonesShown) || undefined}
        data-over={(open && newTrackAt === index) || undefined}
        aria-hidden
        onDragOver={(event) => {
          if (dragging === null || !open) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = "move";
          overGuide(index, event.clientX);
        }}
        onDragLeave={() => setNewTrackAt((at) => (at === index ? null : at))}
        onDrop={(event) => {
          if (dragging === null || !open) return;
          event.preventDefault();
          dropOnGuide(index, event.clientX);
        }}
      />
    );
  };

  // A finger's drag on a phone (spec phone-timeline.md): the browser's drag state and its landing
  // functions, at the place under the finger.
  const scroller = useRef<HTMLDivElement>(null);
  // The band a finger lifted its clip on, and where it was then: the lift opens the new-track
  // guides, which move the bands down under a finger that has not moved (decision 10).
  const liftedBand = useRef<{ trackId: string; top: number; bottom: number } | null>(null);
  const placeAt = (clientX: number, clientY: number): { track: Track } | { guide: number } | null => {
    if (draggedClip === undefined) return null;
    // Over its own band where it is now or where it was at the lift, the finger stays on it: held
    // still or dragged level, it changes no track. A move beyond both is a move off it.
    const held = liftedBand.current;
    if (held !== null) {
      const now = scroller.current?.querySelector(`ul[data-track="${held.trackId}"]`)?.getBoundingClientRect();
      const own = timeline.tracks.find((each) => each.id === held.trackId);
      const top = Math.min(held.top, now?.top ?? held.top);
      const bottom = Math.max(held.bottom, now?.bottom ?? held.bottom);
      if (own !== undefined && clientY >= top && clientY <= bottom) return { track: own };
    }
    const under = document.elementFromPoint(clientX, clientY);
    if (under === null) return null;
    const guide = under.closest<HTMLElement>("[data-new-track]");
    if (guide !== null) {
      const open = draggedClip.kind !== "caption" && !props.readOnly && group === null;
      return open ? { guide: Number(guide.dataset["newTrack"]) } : null;
    }
    const lane = under.closest<HTMLElement>("ul[data-track]");
    const track = timeline.tracks.find((each) => each.id === lane?.dataset["track"]);
    if (track === undefined) return null;
    const accepts = group !== null || TRACK_OF[draggedClip.kind] === track.kind;
    return accepts ? { track } : null;
  };
  const touchDrag: TouchDrag = {
    lift: (block, clientX) => {
      const clip = timeline.clips.find((each) => each.id === block.dataset["clip"]);
      if (clip === undefined || props.readOnly || clip.locked) return false;
      // One of several chosen keeps them chosen: they move together, as the browser's drag does.
      if (!isChosen(selection, clip.id)) select(clip);
      const grab = clientX - block.getBoundingClientRect().left;
      const band = block.closest("ul[data-track]")?.getBoundingClientRect();
      liftedBand.current = band === undefined ? null : { trackId: clip.trackId, top: band.top, bottom: band.bottom };
      setDragging({ id: clip.id, grabMs: Math.max(0, msAt(grab)) });
      zonesTimer.current = window.setTimeout(() => setZonesShown(true), 0);
      return true;
    },
    over: (clientX, clientY) => {
      const place = placeAt(clientX, clientY);
      if (place === null) {
        setLanding(null);
        setGroupLanding(null);
        setNewTrackAt(null);
        setGuideMs(null);
      } else if ("guide" in place) overGuide(place.guide, clientX);
      else overBand(place.track, clientX);
    },
    drop: (clientX, clientY) => {
      const place = placeAt(clientX, clientY);
      liftedBand.current = null;
      if (place === null) endDrag();
      else if ("guide" in place) dropOnGuide(place.guide, clientX);
      else dropOnBand(place.track, clientX);
    },
    cancel: () => {
      liftedBand.current = null;
      endDrag();
    },
  };
  const touched = useTouchTimeline(
    scroller,
    props.touch === undefined
      ? undefined
      : {
          pxPerSecond,
          playheadMs,
          totalMs: total,
          onSeek,
          zoom: props.touch.zoom,
          onZoom: props.touch.onZoom,
          drag: touchDrag,
        },
  );

  return (
    <section className={classes.tracks} aria-label="Tracks" data-touch={props.touch !== undefined || undefined}>
      <div
        ref={scroller}
        className={classes.trackScroll}
        data-touch={props.touch !== undefined || undefined}
        onPointerMove={(event) => {
          if (previewAxis && dragging === null) showAxis(Math.min(timeAt(event.clientX), total));
        }}
        onPointerLeave={() => showAxis(null)}
        // Live's effect tile lands anywhere on the timeline, and goes on a new top track; a text
        // tile lands anywhere too, at the pointer's time.
        onDragOver={(event) => {
          if (!(carriesEffect(event) || carriesText(event)) || props.readOnly) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = "copy";
        }}
        onDrop={(event) => {
          if (props.readOnly) return;
          if (carriesText(event)) {
            event.preventDefault();
            props.onDropText(event.dataTransfer.getData(TEXT_DRAG_TYPE), timeAt(event.clientX));
            return;
          }
          if (!carriesEffect(event)) return;
          event.preventDefault();
          const dropped = event.dataTransfer.getData(EFFECT_DRAG_TYPE);
          const kind = EFFECTS.find((each) => each.kind === dropped)?.kind;
          if (kind != null) props.onDropEffect(kind);
        }}
      >
        <div ref={lanes} className={classes.lanes} style={{ width: px(laneMs) }}>
          <div className={classes.ruler} onPointerDown={seekFrom}>
            {Array.from({ length: Math.floor(laneMs / tickMs) + 1 }, (_, at) => (
              <span
                key={at}
                className={classes.tick}
                data-major={(at * tickMs) % (labelStep * 1000) === 0}
                style={{ left: px(at * tickMs) }}
                aria-hidden
              />
            ))}
            {Array.from({ length: Math.floor(laneMs / 1000 / labelStep) + 1 }, (_, at) => (
              <span
                key={at}
                className={classes.tickLabel}
                data-past={at * labelStep * 1000 > total}
                style={{ left: px(at * labelStep * 1000) }}
                aria-hidden
              >
                {formatTimecode(at * labelStep * 1000)}
              </span>
            ))}
            <input
              type="range"
              className={classes.seek}
              aria-label="Playhead"
              aria-valuetext={formatClock(playheadMs)}
              min={0}
              max={laneMs}
              step={100}
              value={Math.round(playheadMs)}
              onChange={(event) => onSeek(Math.min(Number(event.currentTarget.value), total))}
            />
            <MarkerFlags markers={timeline.markers} px={px} timeAt={timeAt} readOnly={props.readOnly} onSeek={(ms) => onSeek(Math.min(ms, total))} onEdit={onEdit} />
          </div>
          {timeline.tracks.flatMap((track, at) => [newTrackGuide(at), band(track)])}
          {newTrackGuide(timeline.tracks.length)}
          {guideMs === null ? null : (
            <div className={classes.snapGuide} style={{ left: px(guideMs) }} data-snap-guide aria-hidden />
          )}
          <div className={classes.playhead} style={{ left: px(playheadMs) }} aria-hidden />
          {box === null ? null : (
            <div
              className={classes.chooseBox}
              style={{ left: box.left, top: box.top, width: box.width, height: box.height }}
              data-choose-box
              aria-hidden
            />
          )}
          {axisMs === null ? null : (
            <div className={classes.previewAxis} style={{ left: px(axisMs) }} data-preview-axis aria-hidden>
              <span className={classes.previewAxisTime}>{formatTimecode(axisMs, true)}</span>
            </div>
          )}
        </div>
      </div>
      {props.touch === undefined ? null : (
        <>
          {/* The playhead, fixed in the middle: the timeline scrolls under it. */}
          <span className={classes.centreLine} data-centre-line aria-hidden />
          {touched.pinching !== null ? (
            <span className={classes.touchBubble} data-pinch-readout>
              {zoomLabel(touched.pinching)}
            </span>
          ) : touched.lifted ? (
            <span className={classes.touchBubble}>Hold and drag</span>
          ) : null}
        </>
      )}
    </section>
  );
}
